import React, { useEffect, useRef, useState } from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

interface SearchProps {
  queryStringKey?: string;
  expandable?: boolean;
}

// Styled component for the TextField
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop: string) =>
    !['$expandable', '$focused', '$hasValue', '$isLg'].includes(prop),
})<{
  $expandable: boolean;
  $focused: boolean;
  $hasValue: boolean;
  $isLg: boolean;
}>(({ $expandable, $focused, $hasValue, $isLg }) => ({
  width: $isLg && $expandable ? ($focused || $hasValue ? '100%' : '28%') : 'auto',
  transition: 'width 0.3s ease',
}));

function Search({ queryStringKey = 'q', expandable = false }: SearchProps) {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get(queryStringKey);
  const [focused, setFocused] = useState(false);
  const value = search || '';
  const inputRef = useRef<HTMLInputElement | null>(null);

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const setValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      searchParams.delete(queryStringKey);
    } else {
      searchParams.set(queryStringKey, event.target.value);
    }
    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (focused) {
      inputRef.current?.focus();
    }
  }, [focused]);

  const clear = (
    <IconButton
      onClick={() => {
        setValue({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
        inputRef.current?.focus();
      }}
      size="large"
      sx={{ visibility: value ? 'visible' : 'hidden' }}
    >
      <ClearIcon color="disabled" />
    </IconButton>
  );

  return (
    <StyledTextField
      inputRef={inputRef}
      value={value}
      variant="outlined"
      onChange={setValue}
      placeholder={intl.formatMessage({ id: 'SEARCH.INPUT_PLACEHOLDER' })}
      onFocus={() => setFocused(true)}
      onClick={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      $expandable={expandable}
      $focused={focused}
      $hasValue={!!value}
      $isLg={isMd}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="disabled" />
          </InputAdornment>
        ),
        endAdornment: <InputAdornment position="end">{clear}</InputAdornment>,
      }}
      inputProps={{
        'aria-label': 'search',
      }}
    />
  );
}

export default Search;
